import { useState } from 'react';
import { Shield, Lock, Unlock, AlertCircle } from 'lucide-react';
import { useWallet } from '../../contexts/WalletContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';

export function WalletUnlockModal() {
  const { showUnlockModal, setShowUnlockModal, pendingAction, setPendingAction, unlock } = useWallet();
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  const handleUnlock = async () => {
    if (!password || password.length === 0) {
      setError('Por favor, introduza a password da wallet');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Call WalletContext unlock which handles backend call and state update
      const success = await unlock(password);
      
      if (!success) {
        throw new Error('Password incorreta');
      }
      
      // Verify session was actually stored
      const { getWalletSession } = await import('../../auth/walletSession');
      const session = getWalletSession();
      console.log('🔍 Wallet session after unlock:', session ? 'PRESENT' : 'MISSING');
      
      if (!session) {
        throw new Error('Failed to establish wallet session');
      }
      
      toast({
        title: 'Wallet desbloqueada',
        description: 'Pode agora executar ações seguras.',
      });
      setShowUnlockModal(false);
      setPassword('');
      
      // Execute pending action if exists
      // Delay to ensure all state updates are complete and session is available
      if (pendingAction) {
        setTimeout(() => {
          console.log('🚀 Executing pending action with session:', getWalletSession()?.sessionToken?.substring(0, 10) + '...');
          pendingAction();
          setPendingAction(null);
        }, 250);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao desbloquear wallet';
      setError(errorMessage);
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowUnlockModal(false);
    setPassword('');
    setError('');
    setPendingAction(null);
  };

  return (
    <Dialog open={showUnlockModal} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
            <Shield className="h-8 w-8 text-accent" />
          </div>
          <DialogTitle className="text-xl">Desbloquear Wallet</DialogTitle>
          <DialogDescription>
            Introduza a password da wallet para desbloquear e executar ações seguras.
            A wallet permanecerá desbloqueada durante 60 minutos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Input
              type="password"
              placeholder="Introduza a password da wallet"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && password && !isLoading) {
                  handleUnlock();
                }
              }}
              autoFocus
            />
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button 
            variant="wallet" 
            className="flex-1" 
            onClick={handleUnlock}
            disabled={isLoading || !password}
          >
            {isLoading ? (
              <>
                <Lock className="h-4 w-4 mr-2 animate-pulse" />
                A desbloquear...
              </>
            ) : (
              <>
                <Unlock className="h-4 w-4 mr-2" />
                Desbloquear
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
